import React, { Component } from 'react';
import cn from 'classnames';
import scrollTo from '../utils/scrollTo';
import { isEnterKey } from '../utils/checkPressedKey';

interface IProps {
  totalItemsCount: number,
  itemsCountPerPage?: number,
  previousLabel?: React.ReactNode,
  nextLabel?: React.ReactNode,
  onPageChange?: (page: { selected: number }) => void,
  page?: number,
  className?: string,
  inputWrpClassName?: string,
  inputClassName?: string,
  previousClassName?: string,
  nextClassName?: string,
  previousLinkClassName?: string,
  nextLinkClassName?: string,
  disabledClassName?: string,
  scrollOnChange?: React.RefObject<HTMLElement> | string,
  scrollOptions?: { offset?: number, scrollContainerId?: string },
  dataTest: string,
  isDisabled?: boolean,
}

interface IState {
  page: number,
  inputValue: number,
}

const defaultProps: Partial<IProps> = {
  itemsCountPerPage: 10,
  className: 'pagination',
  inputClassName: 'pagination-input',
  disabledClassName: 'is-disabled',
  nextLabel: 'Next',
  nextClassName: 'pagination-arrow pagination-arrow-next',
  previousLabel: 'Previous',
  previousClassName: 'pagination-arrow pagination-arrow-previous',
  scrollOptions: {},
};

class Pagination extends Component<IProps, IState> {
  static defaultProps: Partial<IProps> = defaultProps;

  constructor(props: IProps) {
    super(props);
    this.state = {
      page: this.props.page ? this.props.page : 1,
      inputValue: this.props.page ? this.props.page : 1,
    };
  }

  /**
   * TODO Rewrite this to getDerivedStateFromProps
   * if prevState.page differs from nextProps.page - cache a new shape
   * @param {Object} prevProps
   */
  componentDidUpdate(prevProps) {
    if (prevProps.page !== this.props.page) {
      this.setState({
        page: this.props.page,
        inputValue: this.props.page,
      });
    }
  }

  /**
   * @return {Number}
   */
  getTotalPages = () => Math.ceil(this.props.totalItemsCount / this.props.itemsCountPerPage);

  /**
   * @param {SyntheticEvent} event
   */
  handlePreviousPage = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    } else {
      event.returnValue = false;
    }
    if (this.state.page > 1) {
      this.handlePageSelected(this.state.page - 1, event);
    }
  };

  /**
   * @param {SyntheticEvent} event
   */
  handleNextPage = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    } else {
      event.returnValue = false;
    }
    if (this.state.page < this.getTotalPages()) {
      this.handlePageSelected(this.state.page + 1, event);
    }
  };

  /**
   * @param {Number} page
   * @param {SyntheticEvent} event
   */
  handlePageSelected = (page, event) => {
    if (event.preventDefault) {
      event.preventDefault();
    } else {
      event.returnValue = false;
    }

    if (this.state.inputValue === page) {
      return;
    }

    this.setState({
      inputValue: page,
      page,
    });

    // Call the callback with the new page item:
    this.callCallback(page);
  };

  /**
   * @param {Number} selectedItem
   */
  callCallback = (selectedItem) => {
    if (typeof this.props.onPageChange !== 'undefined' &&
        typeof this.props.onPageChange === 'function') {
      this.props.onPageChange({ selected: selectedItem });
    }

    if (this.props.scrollOnChange) {
      scrollTo(this.props.scrollOnChange, this.props.scrollOptions);
    }
  };

  /**
   * @param {SyntheticEvent} event
   */
  handlePageChange = (event) => {
    this.setState({ inputValue: event.target.value });
  };

  /**
   * @param {SyntheticEvent} event
   */
  handlePageBlur = (event) => {
    let inputValue = parseInt(event.target.value);
    if (!inputValue || inputValue < 1 || inputValue > this.getTotalPages()) {
      inputValue = this.getTotalPages();
    }
    this.handlePageSelected(inputValue, event);
  };

  /**
   * @param {SyntheticEvent} event
   */
  handlePageKeyPress = (event) => {
    if (isEnterKey(event)) {
      this.handlePageBlur(event);
    }
  };

  /**
   * @param {SyntheticEvent} event
   */
  handleHighlightTextOnClick = (event) => event.target.setSelectionRange(0, event.target.value.length);

  /**
   * @return {ReactElement}
   */
  render() {
    const { disabledClassName, dataTest } = this.props;

    const previousCls = cn(this.props.previousClassName, {
      [disabledClassName]: (this.state.page !== this.state.inputValue) ||
                           (this.state.page === 1) ||
                           this.props.isDisabled,
    });

    const nextCls = cn(this.props.nextClassName, {
      [disabledClassName]: (this.state.page !== this.state.inputValue) ||
                           (this.state.page === this.getTotalPages()) ||
                           this.props.isDisabled,
    });

    const inputIsDisabled = ((this.state.page === 1) && (this.state.page === this.getTotalPages())) ||
      this.props.isDisabled;

    return (
      <div
        className={ this.props.className }
        data-test={ `${ dataTest }SelectPageContainer` }
      >
        <div className={ previousCls }>
          <a
            onClick={ this.handlePreviousPage }
            className={ this.props.previousLinkClassName }
            tabIndex={ 0 }
            onKeyPress={ this.handlePreviousPage }
            data-test={ `${ dataTest }PrevButton` }
          >
            { this.props.previousLabel }
          </a>
        </div>
        <div className={ this.props.inputWrpClassName }>
          <input
            type="numeric"
            value={ this.state.inputValue }
            maxLength={ 4 }
            tabIndex={ 0 }
            onChange={ this.handlePageChange }
            onBlur={ this.handlePageBlur }
            onKeyPress={ this.handlePageKeyPress }
            disabled={ inputIsDisabled }
            className={ this.props.inputClassName }
            onClick={ this.handleHighlightTextOnClick }
            data-test={ `${ dataTest }PageInput` }
          />
        </div>
        <div className={ nextCls }>
          <a
            onClick={ this.handleNextPage }
            className={ this.props.nextLinkClassName }
            tabIndex={ 0 }
            onKeyPress={ this.handleNextPage }
            data-test={ `${ dataTest }NextButton` }
          >
            { this.props.nextLabel }
          </a>
        </div>
      </div>
    );
  }
}

export default Pagination;
